import deepEqual from 'fast-deep-equal';
import { Instrument, isInstrumentExpired } from '../instruments';

const strictEqual = (a, b) => a === b;
const dateEqual = (a, b) =>
  a === b || Object.is(new Date(a).getTime(), new Date(b).getTime());

export const DIFF_CONTRACT_DETAILS_FIELDS = [
  ['contractExpiration', dateEqual],
  ['contractName', strictEqual],
  ['governingLaw', strictEqual],
  ['jurisdiction', strictEqual],
  ['bodyOfAgreementType', strictEqual],
];

export const DIFF_INSTRUMENT_DETAILS_FIELDS = [
  ['authorizedQuantity', strictEqual],
  ['tradeRestrictions', deepEqual],
  ['transferFee.amount', Instrument.isEqual],
  ['transferFee.address', strictEqual],
];

export const RESET_INSTRUMENT_DETAILS_FIELDS = [
  'authorizedQuantity',
  'tradeRestrictions',
  'transferFee',
];

export const DIFF_INSTRUMENT_TYPE_SPECIFICS_FIELDS = [
  ['couponName', strictEqual],
  ['programName', strictEqual],
  ['casinoName', strictEqual],
  ['eventName', strictEqual],
  ['venue', strictEqual],
  ['redemptionVenue', strictEqual],
  ['details', strictEqual],
  ['expirationTimestamp', dateEqual],
  ['eventStartTimestamp', dateEqual],
  ['eventEndTimestamp', dateEqual],
  ['faceValue', Instrument.isEqual],
  ['useType', strictEqual],
  ['transfersPermitted', strictEqual],
  ['ageRestriction', deepEqual],
  ['seat', strictEqual],
  ['section', strictEqual],
  ['row', strictEqual],
  ['area', strictEqual],
];

function clausesForAmendmentEquality(arrayOfClauses) {
  if (!Array.isArray(arrayOfClauses)) {
    return arrayOfClauses;
  }
  return arrayOfClauses.map((clause) => ({
    title: clause?.title,
    body: clause?.body,
    children: clausesForAmendmentEquality(clause?.children),
  }));
}

function chaptersForAmendmentEquality(arrayOfClauses) {
  if (!Array.isArray(arrayOfClauses)) {
    return arrayOfClauses;
  }
  return arrayOfClauses.map((clause) => ({
    title: clause?.title,
    preamble: clause?.preamble,
    articles: clausesForAmendmentEquality(clause?.articles),
  }));
}

function definitionsForAmendmentEquality(arrayOfClauses) {
  if (!Array.isArray(arrayOfClauses)) {
    return arrayOfClauses;
  }
  return arrayOfClauses.map((clause) => ({
    term: clause?.term,
    definition: clause?.definition,
  }));
}

export function calculateContractAmendmentDiff(contract = {}) {
  const amendmentDiff = {
    totalChanges: 0,
    totalInstrumentChanges: 0,
  };
  if (!contract) {
    // potentially still loading
    return amendmentDiff;
  }

  const instruments = contract.instruments || [];
  const contractDraft = contract.draft?.details;
  const contractFormation = contract.details;
  if (!contractFormation || !contractDraft) {
    // This is either an unposted draft, or a read-only contract with no
    // visible edits, so nothing to calculate a diff with
    return amendmentDiff;
  }

  // Get differences between contract offer and contract formation
  // The contract formation contains extra information that we could strip out here
  amendmentDiff.detailsDifference = DIFF_CONTRACT_DETAILS_FIELDS.reduce(
    (prevChanges, [fieldName, equalityTest]) => {
      const draftValue = contractDraft?.[fieldName];
      if (!equalityTest(draftValue, contractFormation[fieldName])) {
        return {
          ...prevChanges,
          [fieldName]: draftValue === undefined ? null : draftValue,
        };
      }
      return prevChanges;
    },
    {},
  );
  amendmentDiff.totalHeaderChanges = Object.keys(
    amendmentDiff.detailsDifference,
  ).length;
  amendmentDiff.totalChanges += amendmentDiff.totalHeaderChanges;

  // Check for body of agreement changes
  if (
    contractDraft?.bodyOfAgreementType === 2 &&
    contractFormation.bodyOfAgreementType === 2
  ) {
    if (
      !deepEqual(
        chaptersForAmendmentEquality(
          contract.bodyOfAgreement?.details?.chapters,
        ),
        chaptersForAmendmentEquality(
          contract.draft?.bodyOfAgreement?.details?.chapters,
        ),
      )
    ) {
      amendmentDiff.detailsDifference.chapters =
        contract.draft?.bodyOfAgreement?.details?.chapters;
      amendmentDiff.totalChanges += 1;
    }
    if (
      !deepEqual(
        definitionsForAmendmentEquality(
          contract.bodyOfAgreement?.details?.definitions,
        ),
        definitionsForAmendmentEquality(
          contract.draft?.bodyOfAgreement?.details?.definitions,
        ),
      )
    ) {
      amendmentDiff.detailsDifference.definitions =
        contract.draft?.bodyOfAgreement?.details?.definitions;
      amendmentDiff.totalChanges += 1;
    }
  }

  // Now, let's loop over instruments, find modified ones and new additions
  amendmentDiff.instruments = [];
  instruments.forEach((instrument) => {
    if (!instrument.draft) {
      return;
    }
    if (isInstrumentExpired(instrument)) {
      amendmentDiff.instruments.push({
        typeSpecificsChanges: {},
        detailsChanges: {},
      });
      return;
    }
    if (!instrument.details) {
      // This instrument is a draft meaning the instrument is entirely new addition
      amendmentDiff.instruments.push({
        new: true,
        totalChanges: 5,
        typeSpecificsChanges: {},
        detailsChanges: {},
      });
      amendmentDiff.totalChanges += 5; // number of editable instrument fields when new
      amendmentDiff.totalInstrumentChanges += 5;
    } else {
      // The user has modified a pre-existing instrument, let's find the differences
      const instrumentChanges = {};
      instrumentChanges.detailsChanges = DIFF_INSTRUMENT_DETAILS_FIELDS.reduce(
        (prevChanges, [field, equalityTest]) => {
          const draftValue = instrument.draft.details[field];
          if (!equalityTest(draftValue, instrument.details[field])) {
            return {
              ...prevChanges,
              [field]: draftValue === undefined ? null : draftValue,
            };
          }
          return prevChanges;
        },
        {},
      );
      instrumentChanges.typeSpecificsChanges =
        DIFF_INSTRUMENT_TYPE_SPECIFICS_FIELDS.reduce(
          (prevChanges, [field, equalityTest]) => {
            const draftValue =
              instrument.draft.instrumentTypeSpecifics?.[field];
            if (
              !equalityTest(
                draftValue,
                instrument.instrumentTypeSpecifics[field],
              )
            ) {
              return {
                ...prevChanges,
                [field]: draftValue === undefined ? null : draftValue,
              };
            }
            return prevChanges;
          },
          {},
        );
      const typeSpecificsChangeCount = Object.keys(
        instrumentChanges.typeSpecificsChanges,
      ).length;
      amendmentDiff.totalChanges += typeSpecificsChangeCount;
      amendmentDiff.totalInstrumentChanges += typeSpecificsChangeCount;
      const detailsChangeCount = Object.keys(
        instrumentChanges.detailsChanges,
      ).length;
      amendmentDiff.totalChanges += detailsChangeCount;
      amendmentDiff.totalInstrumentChanges += detailsChangeCount;
      instrumentChanges.totalChanges =
        typeSpecificsChangeCount + detailsChangeCount;

      amendmentDiff.instruments.push(instrumentChanges);
    }
  });
  // console.debug({ amendmentDiff, contract });
  return amendmentDiff;
}
